import React, { useState, useEffect, useRef } from "react";

import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import { i18n } from "../../translate/i18n";

import { Slider, Stack } from "@mui/material";
import { GitBranch, Shuffle } from "lucide-react";
import ChatiaDialogActions from "../ChatiaDialogActions";
import { CloseXIcon } from "../ChatiaIcons";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette.type === "dark";

  return {
    root: {
      display: "flex",
      flexWrap: "wrap",
    },
    dialogPaper: {
      ...(theme.chatiaDS?.dialogPaper || {}),
      "& .MuiDialogContent-root": {
        flex: 1,
        overflowY: "auto !important",
        overflowX: "hidden",
        background: isDark ? "#121212" : "#ffffff",
        ...theme.scrollbarStyles,
      },
      "& .MuiDialogActions-root": {
        flexShrink: 0,
        padding: "12px 16px",
        background: isDark ? "#121212" : "#ffffff",
      },
    },
    header: {
      ...(theme.chatiaDS?.header || {}),
      flexShrink: 0,
    },
    headerTitle: {
      ...(theme.chatiaDS?.headerTitle || {}),
    },
    closeButton: {
      backgroundColor: theme.chatiaDS?.closeButton?.backgroundColor || "rgba(255,255,255,0.08)",
      color: theme.chatiaDS?.closeButton?.color || "#ffffff",
      transition: theme.chatiaDS?.closeButton?.transition || "all 0.2s ease",
      "&:hover": {
        backgroundColor: theme.chatiaDS?.closeButtonHover?.backgroundColor || "rgba(255,255,255,0.18)",
        color: "#ffffff",
      },
    },
    content: {
      padding: "18px 20px !important",
      maxHeight: "70vh",
    },
    bodyCard: {
      gap: 12,
      display: "flex",
      flexDirection: "column",
    },
    infoCard: {
      background: theme.mode === "dark" ? "#16191f" : "#ffffff",
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
      border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
      borderRadius: 12,
      padding: 16,
      boxShadow: "0 10px 28px rgba(0, 0, 0, 0.16)",
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 14,
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: 0.1,
    },
    cardIcon: {
      width: 32,
      height: 32,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.04)",
      border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
      color: theme.palette.primary.main,
      "& svg": {
        color: theme.palette.primary.main,
        stroke: theme.palette.primary.main,
      },
    },
    percentBadge: {
      minWidth: 62,
      height: 38,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: theme.mode === "dark" ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.04)",
      border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
      fontWeight: 800,
    },
  };
});

const FlowBuilderRandomizerModal = ({
  open,
  onSave,
  data,
  onUpdate,
  close
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMounted = useRef(true);

  const [percent, setPercent] = useState(0);
  const [activeModal, setActiveModal] = useState(false);
  

  useEffect(() => {
    if (open === "edit") {
      setPercent(data.data.percent);
      setActiveModal(true);
    } else if (open === "create") {
      setPercent(0);
      setActiveModal(true);
    }
    return () => {
      isMounted.current = false;
    };
  }, [open]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleValue = (event, newValue) => {
    setPercent(newValue)
  }

  const handleSaveContact = async values => {
    if (!percent || parseInt(percent) <= 0) {
      return toast.error(i18n.t("flowBuilderConfig.modals.randomizer.addIntervalError"));
    }
    if (parseInt(percent) > 120) {
      return toast.error(i18n.t("flowBuilderConfig.modals.randomizer.maxTimeError"));
    }
    if (open === "edit") {
      onUpdate({
        ...data,
        data: { percent: percent }
      });
    } else if (open === "create") {
      onSave({
        percent: percent
      });
    }
    handleClose();
  };

  const title = open === "create"
    ? i18n.t("flowBuilderModals.randomizerModal.titleAdd")
    : i18n.t("flowBuilderModals.randomizerModal.titleEdit");

  const CardTitle = ({ icon: Icon, children }) => (
    <div className={classes.cardHeader}>
      <span className={classes.cardIcon}>
        <Icon size={16} />
      </span>
      <span>{children}</span>
    </div>
  );

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>{title}</Typography>
          <IconButton size="small" onClick={handleClose} className={classes.closeButton}>
            <CloseXIcon style={{ fontSize: 18 }} />
          </IconButton>
        </div>
        <Stack>
          <DialogContent className={classes.content}>
            <Stack className={classes.bodyCard}>
              <Stack className={classes.infoCard}>
                <CardTitle icon={Shuffle}>
                  {i18n.t("flowBuilderModals.randomizerModal.title")}
                </CardTitle>
                <Stack direction="row" minHeight={120} alignItems="center" gap={3}>
                  <Typography className={classes.percentBadge}>{percent}%</Typography>
                  <Slider
                    aria-label={i18n.t("flowBuilderModals.randomizerModal.sliderLabel")}
                    defaultValue={percent}
                    value={percent}
                    valueLabelDisplay="auto"
                    onChange={handleValue}
                    step={10}
                    marks
                    min={0}
                    max={100}
                  />
                  <Typography className={classes.percentBadge}>{100 - percent}%</Typography>
                </Stack>
              </Stack>

              <Stack className={classes.infoCard}>
                <CardTitle icon={GitBranch}>
                  {i18n.t("flowBuilderModals.randomizerModal.outputsTitle")}
                </CardTitle>
                <Typography style={{ color: theme.mode === "dark" ? "#b8c1cc" : "#667781", fontSize: 13 }}>
                  {i18n.t("flowBuilderModals.randomizerModal.outputsHint")}
                </Typography>
              </Stack>
            </Stack>
          </DialogContent>
          <ChatiaDialogActions
            type="button"
            onClick={() => handleSaveContact()}
            saveText={open === "create"
              ? i18n.t("flowBuilderModals.randomizerModal.buttonAdd")
              : i18n.t("flowBuilderModals.randomizerModal.buttonEdit")}
          />
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderRandomizerModal;
